import styled from 'styled-components';

export const PageRoot = styled.div`
  display: flex;
  /* Fill the available content area without making the surrounding page scroll.
     Height is the viewport minus the chrome above (breadcrumb + unified
     header + selectors). Panels sit edge-to-edge (no gray outer padding or
     gap) so the sidebar's right border becomes the visual separator. */
  height: calc(100vh - 105px);
  flex: 1;
  background: #ffffff;
  box-sizing: border-box;
`;

export const Sidebar = styled.aside`
  width: 280px;
  flex-shrink: 0;
  background: #ffffff;
  /* Sidebar carries the only vertical separator between the library and the
     table panel — no rounded corners, no outer ring. */
  border-right: 1px solid #e9ecef;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
`;

export const SidebarBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

export const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #868e96;
  border-bottom: 1px solid #e9ecef;
`;

export const SidebarSearch = styled.div`
  padding: 10px 12px 8px;
  > input {
    width: 100%;
    height: 32px;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    padding: 0 10px 0 32px;
    font-size: 13px;
    background: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23868e96' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><circle cx='11' cy='11' r='8'/><line x1='21' y1='21' x2='16.65' y2='16.65'/></svg>")
      no-repeat 10px center;
    color: #495057;
  }
  > input:focus {
    outline: none;
    border-color: #228be6;
  }
`;

export const SidebarSection = styled.div`
  padding: 4px 0;

  /* Spacing between adjacent sections (e.g. General → Custom). */
  & + & {
    margin-top: 8px;
  }
`;

/* Static section label matching V2's `.sectionLabel` — no longer a button,
 * since the V2 design drops collapse affordances in favour of a flat list. */
export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px 4px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #adb5bd;

  > .left {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
`;

export const ItemList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

/* V2 `.lookupItem` — flat row, 3 px blue left-border when active. */
export const Item = styled.li<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px 7px 20px;
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? 500 : 400)};
  color: ${({ $active }) => ($active ? '#1971c2' : '#495057')};
  cursor: pointer;
  background: ${({ $active }) => ($active ? '#e7f5ff' : 'transparent')};
  border-left: 3px solid ${({ $active }) => ($active ? '#228be6' : 'transparent')};
  transition: background 100ms ease;

  &:hover {
    background: ${({ $active }) => ($active ? '#e7f5ff' : '#f8f9fa')};
  }
`;

/* Icon prefix on each item (matches V2's `.lookupItemIcon`). */
export const ItemIcon = styled.span<{ $active?: boolean }>`
  display: inline-flex;
  flex-shrink: 0;
  color: ${({ $active }) => ($active ? '#228be6' : '#ced4da')};
`;

export const ItemRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  > .delete {
    border: none;
    background: transparent;
    color: #adb5bd;
    cursor: pointer;
    padding: 0;
    display: inline-flex;
    align-items: center;
  }
  > .delete:hover {
    color: #fa5252;
  }
`;

export const Main = styled.section`
  flex: 1;
  min-width: 0;
  min-height: 0;
  background: #ffffff;
  /* Flat edge-to-edge — separator comes from the Sidebar's right border. */
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

/* V2 `.tableHeader` — 36 px tall, hairline border, title on the left,
 * toolbar buttons crammed against the right edge. */
export const MainHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 24px;
  min-height: 36px;
  border-bottom: 1px solid #e9ecef;
  font-size: 14px;
  font-weight: 600;
  color: #101113;
  flex-shrink: 0;
`;

export const MainActions = styled.div`
  display: flex;
  gap: 8px;
`;

/* V2 `compact-xs` Mantine button size — slimmer than the previous 32 px row.
 * Visually matches the reference toolbar with Add Row / Import / Export. */
export const ToolBtn = styled.button<{ $primary?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 26px;
  padding: 0 10px;
  border: 1px solid ${({ $primary }) => ($primary ? '#228be6' : '#dee2e6')};
  border-radius: 4px;
  background: ${({ $primary }) => ($primary ? '#228be6' : '#ffffff')};
  color: ${({ $primary }) => ($primary ? '#ffffff' : '#495057')};
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;

  &:hover:not(:disabled) {
    background: ${({ $primary }) => ($primary ? '#1c7ed6' : '#f8f9fa')};
    border-color: ${({ $primary }) => ($primary ? '#1c7ed6' : '#adb5bd')};
  }
  &:disabled {
    cursor: not-allowed;
    color: ${({ $primary }) => ($primary ? '#ffffff' : '#adb5bd')};
    background: ${({ $primary }) => ($primary ? '#a5d8ff' : '#ffffff')};
    border-color: ${({ $primary }) => ($primary ? '#a5d8ff' : '#dee2e6')};
  }
`;

export const ShortcutsHint = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0 12px;
  font-size: 12px;
  font-weight: 400;
  color: #868e96;
  white-space: nowrap;

  > kbd {
    display: inline-flex;
    align-items: center;
    height: 18px;
    padding: 0 5px;
    border: 1px solid #dee2e6;
    border-bottom-width: 2px;
    border-radius: 4px;
    background: #f8f9fa;
    color: #495057;
    font-family: inherit;
    font-size: 11px;
    font-weight: 500;
    line-height: 1;
  }
`;

export const TableWrap = styled.div`
  flex: 1;
  min-height: 0;
  /* Only the table rows scroll — header and footer stay pinned. */
  overflow-y: auto;
  overflow-x: auto;
  max-height: calc(100vh - 280px);
  min-height: calc(100vh - 320px);
`;

export const Grid = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;

  th,
  td {
    border-right: 1px solid #f1f3f5;
    border-bottom: 1px solid #f1f3f5;
    padding: 10px 12px;
    text-align: left;
    color: #212529;
    vertical-align: middle;
  }
  th:first-child,
  td:first-child {
    width: 48px;
    text-align: center;
    color: #868e96;
    background: #fafbfc;
  }
  thead th {
    /* Sticky column header row — stays visible while rows scroll. */
    position: sticky;
    top: 0;
    z-index: 1;
    background: #fafbfc;
    color: #495057;
    font-weight: 600;
  }
  tr:hover > td {
    background: #fafbfc;
  }
`;

export const HeaderCell = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  > .type {
    display: inline-flex;
    align-items: center;
    color: #adb5bd;
  }
`;

export const CellInput = styled.input`
  width: 100%;
  border: none;
  background: transparent;
  font: inherit;
  color: inherit;
  padding: 0;

  &:focus {
    outline: 1px solid #228be6;
    outline-offset: -1px;
    background: #ffffff;
  }
`;

export const AddRowFooter = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: #ffffff;
  font-size: 13px;
  font-style: italic;
  color: #868e96;
  cursor: pointer;
  border-top: 1px dashed #dee2e6;
  flex-shrink: 0;

  &:hover {
    color: #228be6;
    background: #f8f9fa;
  }
`;

export const RowDeleteButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: #adb5bd;
  cursor: pointer;
  padding: 0;
  line-height: 0;

  &:hover {
    color: #fa5252;
  }
`;
